/**
 * <InlineVoiceRecorder> — enregistrer un vocal sur place, dans la carte qui le demande (organism).
 *
 * Le bilan du tuteur enregistrait dans la page (l'ancien `AudioRecordingContainer`) ; le portage
 * l'avait mis dans une feuille (`<VoiceRecorderSheet>`) : « Enregistrer un vocal », puis la feuille,
 * puis « Envoyer » — trois gestes là où il en fallait un (Wajih, 03/11). Ici, l'enregistrement court
 * à sa place : le compteur (`<RecorderClock>`) et la ligne d'état, « Pause » ou « Reprendre », et
 * « Terminer », qui rend le vocal à l'écran — c'est lui qui l'envoie aussitôt, comme l'ancien bilan.
 * « Annuler » abandonne. Le micro refusé et l'échec se disent dans la carte, avec leur geste.
 *
 * Un enregistrement que le moteur a arrêté seul (l'app quittée, la durée maximale) reste dans la carte,
 * sa phrase au-dessus : « Réenregistrer » ou « Envoyer » — rien ne part sans le tuteur.
 *
 * Il ne montre rien au repos (`idle`), ni une fois le vocal fini par « Terminer » (`recorded` sans
 * phrase : il part) : le bouton qui lance l'enregistrement appartient à la carte.
 * Contrôlé : l'état, la durée et l'arrêt en cours arrivent en props, chaque geste remonte. Le moteur
 * est `engine/voice/useVoiceRecorder`, posé par l'écran.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { RecorderClock, recorderNoticeText } from "../../molecules/RecorderClock"
import { type UIVoiceRecorderNotice, type UIVoiceRecorderPhase } from "../../../ports/primary/ui/types/studentVoice"

export type InlineVoiceRecorderProps = {
    phase: UIVoiceRecorderPhase
    /** La durée figée au dernier changement, en ms. */
    durationMs: number
    /** Le début du segment en cours (`Date.now()`) : le compteur court tout seul. */
    runningSince?: number | null
    maxDurationMs?: number
    notice?: UIVoiceRecorderNotice | null
    /** Ce que dit une fin demandée trop tôt (`notice: "too_short"`). */
    tooShortMessage?: string
    /** L'arrêt est demandé, le fichier s'écrit : « Terminer » tourne. */
    stopping?: boolean
    onPause: () => void
    onResume: () => void
    /** « Terminer » : le vocal est fini, l'écran l'envoie. */
    onStop: () => void
    /** « Annuler » : rien ne part. */
    onCancel: () => void
    /** `denied` / `error` : redemander le micro, ou relancer. */
    onRetry: () => void
    /** `recorded` après un arrêt du moteur : envoyer ce qui est gardé. */
    onSend: () => void
    /** `recorded` après un arrêt du moteur : tout reprendre. */
    onRedo: () => void
    /** `denied` pour de bon : ouvrir les réglages de l'appareil, quand l'écran le peut. */
    onOpenSettings?: () => void
    testID?: string
}

/**
 * L'enregistreur a quelque chose à montrer : tout sauf le repos et un vocal fini par « Terminer »
 * (`recorded` sans phrase, qui part aussitôt). Le champ qui le porte et le crochet qui envoie lisent
 * cette même règle.
 */
export function isRecorderOpen(
    recorder: Pick<InlineVoiceRecorderProps, "phase" | "notice"> | null | undefined
): boolean {
    if (recorder == null || recorder.phase === "idle") return false
    return recorder.phase !== "recorded" || recorder.notice != null
}

const TEXTS = {
    requesting: "On demande l'accès au micro…",
    recording: "Enregistrement en cours",
    paused: "En pause",
    pause: "Pause",
    resume: "Reprendre",
    stop: "Terminer",
    cancel: "Annuler",
    denied: "Autorise l'accès au micro pour enregistrer un vocal.",
    deniedSettings: "Autorise l'accès au micro dans les réglages, puis reviens ici.",
    error: "L'enregistrement n'a pas marché. Réessaie.",
    settings: "Ouvrir les réglages",
    retry: "Réessayer",
    send: "Envoyer",
    redo: "Réenregistrer",
}

export function InlineVoiceRecorder(props: InlineVoiceRecorderProps) {
    const {
        phase,
        durationMs,
        runningSince,
        maxDurationMs,
        notice,
        tooShortMessage,
        stopping = false,
        onPause,
        onResume,
        onStop,
        onCancel,
        onRetry,
        onSend,
        onRedo,
        onOpenSettings,
        testID,
    } = props

    const noticeText = recorderNoticeText(notice, maxDurationMs, tooShortMessage)

    if (!isRecorderOpen(props)) return null
    if (phase === "recorded") {
        return (
            <Stack gap="3" style={styles.box} testID={testID}>
                {noticeText != null && <AlertCard severity="info" message={noticeText} />}
                <Row gap="3">
                    <View style={styles.half}>
                        <Button variant="outline" label={TEXTS.redo} iconName="redo" onPress={onRedo} fullWidth />
                    </View>
                    <View style={styles.half}>
                        <Button label={TEXTS.send} iconName="telegram-plane" onPress={onSend} fullWidth />
                    </View>
                </Row>
                <Stack align="center">
                    <TextButton label={TEXTS.cancel} color="text-muted" onPress={onCancel} />
                </Stack>
            </Stack>
        )
    }

    if (phase === "denied" || phase === "error") {
        const toSettings = phase === "denied" && onOpenSettings != null
        return (
            <Stack gap="2" testID={testID}>
                <AlertCard
                    severity="alert"
                    message={
                        phase === "error" ? TEXTS.error : onOpenSettings != null ? TEXTS.deniedSettings : TEXTS.denied
                    }
                />
                <Row gap="3" justify="center" align="center">
                    <TextButton label={TEXTS.cancel} color="text-muted" onPress={onCancel} />
                    <Button
                        label={toSettings ? TEXTS.settings : TEXTS.retry}
                        variant="outline"
                        size="sm"
                        onPress={toSettings ? onOpenSettings : onRetry}
                    />
                </Row>
            </Stack>
        )
    }

    return (
        <Stack gap="3" style={styles.box} testID={testID}>
            {noticeText != null && <AlertCard severity="warning" message={noticeText} />}
            <RecorderClock
                durationMs={durationMs}
                runningSince={phase === "recording" && !stopping ? runningSince : null}
                maxDurationMs={maxDurationMs}
            />
            <Row gap="2" align="center" justify="center">
                {phase === "recording" && <View style={styles.dot} />}
                <Text size="text-md" weight="semibold" color={phase === "recording" ? "text-danger" : "text-muted"}>
                    {phase === "requesting" ? TEXTS.requesting : phase === "recording" ? TEXTS.recording : TEXTS.paused}
                </Text>
            </Row>
            {phase !== "requesting" && (
                <Row gap="3">
                    <View style={styles.half}>
                        <Button
                            variant="outline"
                            label={phase === "recording" ? TEXTS.pause : TEXTS.resume}
                            iconName={phase === "recording" ? "pause" : "microphone"}
                            onPress={phase === "recording" ? onPause : onResume}
                            disabled={stopping}
                            fullWidth
                        />
                    </View>
                    <View style={styles.half}>
                        <Button label={TEXTS.stop} iconName="check" onPress={onStop} loading={stopping} fullWidth />
                    </View>
                </Row>
            )}
            <Stack align="center">
                <TextButton label={TEXTS.cancel} color="text-muted" onPress={onCancel} />
            </Stack>
        </Stack>
    )
}

const DOT = 10

const styles = StyleSheet.create((theme) => ({
    box: {
        backgroundColor: theme.colors["surface-subtle"],
        borderRadius: theme.radius.md,
        padding: theme.spacing["3"],
    },
    dot: { width: DOT, height: DOT, borderRadius: DOT / 2, backgroundColor: theme.colors.danger },
    half: { flex: 1 },
}))
